/**
 * Adapted from Domo documentation:
 * https://knowledge.domo.com/Engage/Sharing_Content_Outside_of_Domo/Sharing_Cards_Outside_of_Domo_Using_Domo_Embed#Overcoming_Safari's_third-party_cookie_policy
 *
 * Before loading a domo embedded card or dashboard into an iframe:
 * > import domo from '@/utils/domo';
 * > const ready = await domo.hasCookieSupport();
 * > if (!ready) domo.bouncePage();
 *
 * This will redirect you to a Domo hosted page that will set the required
 * cookies needed by the iframe and redirect you back to the current href.
 */

import { getCookie } from "@/utils/cookie";
import { Storage } from "@/utils/storage";

export const DOMO_ROOT_DOMAIN = "https://k4connect.domo.com";
export const DOMO_SESSION_MARKER = "k4ops_safari_fix";

// Session store
const storage = Storage.session();

/**
 * bouncePage
 *
 * Redirect current page to domo bounce page which sets required cookies
 * directly. Session token ensures that this only happens once during this
 * browser session. (A page session lasts as long as the browser is open, and
 * survives over page reloads and restores.)
 */
export function bouncePage() {
	setSessionMarker();
	window.location = `${DOMO_ROOT_DOMAIN}/safari-special`;
}

/**
 * hasCookieSupport
 *
 * Check for CORS support and verify that a cookie has been set on the window context (for Domo) at least once
 * for Safari support. Once this method has been called, it shouldn't need to be reused until the
 * browser's cookies/history has been cleared.
 */
export async function hasCookieSupport() {
	if (getSessionMarker()) {
		if (window.vm.$log)
			window.vm.$log(
				"Cookie support check has already run in this session. Skipping."
			);
		return true;
	}

	try {
		// Create request for setting 3rd party cookie
		await get(`${DOMO_ROOT_DOMAIN}/cookieSupport?action=setCookie`);

		// CORS may have worked, but we need to verify cookie value
		await checkCookie();
	} catch (error) {
		return false;
	}

	return true;
}

/**
 * Clear session marker, forcing a recheck of cookie support and potential
 * redirect to DOMO bounce page. This should only be called in the case of a
 * user logging out.
 */
export function clearSession() {
	storage.remove(DOMO_SESSION_MARKER);
}

function setSessionMarker(val = true) {
	storage.set(DOMO_SESSION_MARKER, val);
}

function getSessionMarker() {
	return storage.get(DOMO_SESSION_MARKER);
}

/**
 * checkCookie
 *
 * Call the `cookieSupport?action=setCookie&cookieValue={cookieValue}` endpoint to verify that
 * the original call to set the cookie was done correctly.
 */
async function checkCookie() {
	// Attempt to retrieve the cookie value
	var cookieValue = getCookie("safari");

	if (!cookieValue) throw new Error(`Cookie not found or falsy`);

	let resp;
	try {
		// Create a request to check the cookie value
		const xhr = await get(
			`${DOMO_ROOT_DOMAIN}/cookieSupport?action=setCookie&cookieValue=${cookieValue}`
		);
		resp = JSON.parse(xhr.responseText);
	} catch (error) {
		throw new Error(`There was an issue checking the cookie value: ${error}`);
	}

	if (!resp) {
		// If it is not 'true'
		throw new Error("Wrong cookie value");
	}

	return true;
}

/**
 * get
 * @param {String} url : a URL to request from using a GET method
 */
function get(url) {
	return fetch(url, {
		method: "get",
		credentials: "include",
	});
}

export default {
	DOMO_ROOT_DOMAIN,
	DOMO_SESSION_MARKER,

	bouncePage,
	clearSession,
	hasCookieSupport,
};
